import classnames from 'classnames';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
import {
    MORE_ITEMS,
    ITEM_DETAILS,
    ABOUT_SELLER,
    BUYER_INCENTIVES,
    labelMap,
} from '../../helpers/soldPdpConstants';
import { dibsLazy } from 'dibs-lazy/src/dibsLazy';
import { graphql, useFragment } from 'react-relay';
import slugify from 'dibs-slugify/exports/slugify';
import { FC, ReactNode, useState, useCallback, MouseEventHandler, Suspense } from 'react';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';
import { sellerInformation, buyerIncentives, itemDetails, moreItems } from '../../pdpMessages';

// components
import { Link } from 'dibs-elements/exports/Link';
import { TabList } from 'dibs-elements/exports/TabList';
import { useTab } from 'dibs-elements/exports/useTab';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { PdpRespSharedAboutItemPanel } from '../../respShared/PdpRespSharedAboutItemPanel/PdpRespSharedAboutItemPanel';
import { PdpRespSharedStoryTellingTab } from '../../respShared/PdpRespSharedStoryTelling/PdpRespSharedStoryTellingTab';
import {
    PdpSharedStorytellingTrackingWrapper,
    PdpSharedStorytellingTrackingContextProvider,
} from '../../shared/PdpSharedStoryTelling/PdpSharedStoryTellingTracking';
import { useBuyerIncentivesV3ClientVariants } from 'dibs-buyer-incentives/exports/buyerIncentivesV3Helpers';

// styles
import styles from './PdpRespUnavailableDetailsTabs.scss';

// types
import { PdpRespUnavailableDetailsTabs_item$key as Item } from './__generated__/PdpRespUnavailableDetailsTabs_item.graphql';
import { PdpRespUnavailableDetailsTabs_user$key as User } from './__generated__/PdpRespUnavailableDetailsTabs_user.graphql';
import { PdpRespUnavailableDetailsTabs_viewer$key as Viewer } from './__generated__/PdpRespUnavailableDetailsTabs_viewer.graphql';

// lazy components
const PdpRespSharedSellerDetails = dibsLazy(
    'PdpUnavailableSellerAccessible',
    () =>
        import(
            /* webpackChunkName: "PdpUnavailableSellerAccessible" */ '../../respShared/PdpRespSharedSellerDetails/PdpRespSharedSellerDetails'
        )
);
const PdpSharedBuyerIncentives = dibsLazy('PdpSharedBuyerIncentives', () =>
    import(
        /* webpackChunkName: "PdpSharedBuyerIncentives" */ '../../shared/PdpSharedBuyerIncentives/PdpSharedBuyerIncentives'
    ).then(mod => ({ default: mod.PdpSharedBuyerIncentives }))
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableDetailsTabs_viewer on Viewer {
        ...PdpRespSharedSellerDetails_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespUnavailableDetailsTabs_user on User {
        ...PdpRespSharedAboutItemPanel_user
        ...PdpRespSharedSellerDetails_user
        ...PdpSharedBuyerIncentives_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespUnavailableDetailsTabs_item on Item {
        storyTelling: storyTellingModule(shortTitles: true) {
            tabs {
                tabTitle
                ...PdpRespSharedStoryTellingTab_storyTellingTab
            }
        }
        seller {
            isAccessible
        }
        ...PdpRespSharedAboutItemPanel_item
        ...PdpRespSharedSellerDetails_item
    }
`;

const CustomTab: FC<{ name: string; dataTn?: string; children: ReactNode }> = ({
    name,
    dataTn,
    children,
}) => {
    const { tabCallbackRef, handleClick, isActive } = useTab(name);
    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            handleClick();
            trackEvent(
                {
                    category: 'product',
                    action: 'sold pdp tab clicked',
                    label: labelMap[name],
                    eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                    interaction_type: interactionTypeConstants.INTERACTION_TYPE_PDP,
                    step_interaction_name:
                        stepInteractionConstants.STEP_INTERACTION_PDP_TAB_CLICKED,
                    trigger: labelMap[name],
                },
                event
            );
        },
        [handleClick, name]
    );

    return (
        <li className={styles.navTabItem}>
            <Link
                dataTn={dataTn}
                onClick={onClick}
                linkRef={tabCallbackRef}
                buyerLinkType="linkNoDecoration"
                className={classnames(styles.navTabLink, { [styles.active]: isActive })}
            >
                {children}
            </Link>
        </li>
    );
};

export const PdpRespUnavailableDetailsTabs: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null | undefined;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const {
        currency,
        relayVars: { afterDisplay },
    } = useRespUnavailableContext();
    const isBuyerIncentivesClientVariant = useBuyerIncentivesV3ClientVariants({ isMobile: false });

    const [activeTab, setActiveTab] = useState(MORE_ITEMS);
    const tabs = item?.storyTelling?.tabs || [];
    const isSellerAccessible = item?.seller?.isAccessible;
    return (
        <PdpSharedStorytellingTrackingContextProvider>
            <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                <TabList fullWidth customTab indicatorPadding="xSmall">
                    {[
                        [
                            MORE_ITEMS,
                            <CustomTab key={MORE_ITEMS} name={MORE_ITEMS} dataTn="more-items-tab">
                                {moreItems}
                            </CustomTab>,
                        ],
                        [
                            ITEM_DETAILS,
                            <CustomTab
                                key={ITEM_DETAILS}
                                name={ITEM_DETAILS}
                                dataTn="item-details-tab"
                            >
                                {itemDetails}
                            </CustomTab>,
                        ],
                        ...tabs.reduce<[string, ReactNode][]>((acc, tab) => {
                            if (tab) {
                                acc.push([
                                    tab.tabTitle,
                                    <CustomTab
                                        key={tab.tabTitle}
                                        name={tab.tabTitle}
                                        dataTn={`${slugify(tab.tabTitle)}-tab`}
                                    >
                                        {tab.tabTitle}
                                    </CustomTab>,
                                ]);
                            }
                            return acc;
                        }, []),
                        isSellerAccessible && [
                            ABOUT_SELLER,
                            <CustomTab
                                key={ABOUT_SELLER}
                                name={ABOUT_SELLER}
                                dataTn="about-the-seller-tab"
                            >
                                {sellerInformation}
                            </CustomTab>,
                        ],
                        isBuyerIncentivesClientVariant && [
                            BUYER_INCENTIVES,
                            <CustomTab
                                key={BUYER_INCENTIVES}
                                name={BUYER_INCENTIVES}
                                dataTn="buyer-incentives-tab"
                            >
                                {buyerIncentives}
                            </CustomTab>,
                        ],
                    ]}
                </TabList>
                <div
                    className={classnames(styles.panels, {
                        [styles.wrapper]: activeTab !== MORE_ITEMS,
                    })}
                >
                    <div
                        className={classnames({
                            [styles.innerPanel]: activeTab !== MORE_ITEMS,
                        })}
                    >
                        <TabPanels renderOnInactive>
                            {[
                                [MORE_ITEMS, null],
                                [
                                    ITEM_DETAILS,
                                    <PdpRespSharedAboutItemPanel
                                        user={user}
                                        item={item}
                                        currency={currency}
                                        selectedSkuId={null}
                                        showHeader={false}
                                        showExpandingArea={activeTab === ITEM_DETAILS}
                                        afterDisplay={afterDisplay}
                                        showThirdCol={false}
                                    />,
                                ],
                                ...tabs.reduce<[string, ReactNode][]>((acc, tab) => {
                                    if (tab) {
                                        acc.push([
                                            tab.tabTitle,
                                            <PdpSharedStorytellingTrackingWrapper
                                                key={tab.tabTitle}
                                            >
                                                <PdpRespSharedStoryTellingTab
                                                    storyTellingTab={tab}
                                                    clearBackground={false}
                                                    showExpandingArea={activeTab === tab.tabTitle}
                                                    dataTn={`${slugify(tab.tabTitle)}-tab-content`}
                                                    wrapperClass={styles.storyTellingWrapper}
                                                />
                                            </PdpSharedStorytellingTrackingWrapper>,
                                        ]);
                                    }
                                    return acc;
                                }, []),
                                isSellerAccessible && [
                                    ABOUT_SELLER,
                                    <Suspense fallback={null}>
                                        <PdpRespSharedSellerDetails
                                            sku={null}
                                            user={user}
                                            item={item}
                                            viewer={viewer}
                                            currency={currency}
                                            dataTn="about-the-seller-tab-content"
                                        />
                                    </Suspense>,
                                ],
                                isBuyerIncentivesClientVariant && [
                                    BUYER_INCENTIVES,
                                    <Suspense fallback={null}>
                                        <PdpSharedBuyerIncentives user={user} />,
                                    </Suspense>,
                                ],
                            ]}
                        </TabPanels>
                    </div>
                </div>
            </TabProvider>
        </PdpSharedStorytellingTrackingContextProvider>
    );
};
